import   React       ,
       { useState    ,
	     useRef      ,
		 ChangeEvent }  from "react";

import   classNames     from "classnames";

import   styles         from "./Field.module.scss";

import   Icon           from "../images/Icon";
import { getMediaType } from "./FieldMedia";
import   SoundPlayer    from "../players/SoundPlayer";
import   ProgressBar ,
       { DownloadProgress } from "../gauge/ProgressBar";

export interface FieldSoundProps {
  placeholder?    : string;
  value?          : string | number;
  mediaURL?       : string; 
  readOnly?       : boolean;
  onChange?       : ( event: React.ChangeEvent<HTMLInputElement> , value: string | number | File ) => void;
  /** Appelé quand le fichier choisi n'est pas un son : il est refusé avant tout aperçu,
      et rien n'est remonté par `onChange`. */
  onFileRejected? : ( file: File ) => void;
  onDelete?       : () => void;
  onShowLibrary?  : () => void;
  isLoading?      : boolean;
  loadProgress?   : DownloadProgress | null;
  width?          : string;
  height?         : string;
  glowColor?      : string;
  iconSquareColor?: string;
  baseURL?        : string;
  extension?      : string;
  showPreview?    : boolean;
  autoPlay?       : boolean;
  library?        : boolean;
  upload?         : boolean;
  /** À false, la zone de texte disparaît et le lecteur prend sa place sur la ligne. */
  showTextInput?  : boolean;
  /**
   * Largeur fixe du lecteur, quand il occupe la ligne (`showTextInput` à false).
   * Sans elle il prend toute la place laissée par les icônes ; avec elle il s'aligne
   * sur un aperçu voisin de même largeur — c'est ce que fait la mire d'attente, dont
   * l'image et le son doivent se répondre.
   */
  previewWidth?   : string;

  /** Nom du fichier tel qu'il a été téléversé, affiché sur la ligne des icônes. */
  fileName?       : string;
}

export const FieldSound = ({ placeholder                   ,
                             value           = ""            ,
                             mediaURL        = '',
                             readOnly                        ,
                             onChange                        ,
                             onFileRejected                  ,
                             onDelete                        ,
                             onShowLibrary                   ,
                             isLoading       = false         ,
                             loadProgress    = null          ,
                             width           = "100%"        ,
                             height          = "fit-content" ,
                             glowColor       = "var(--color-Field-Focus-normal)",
                             iconSquareColor = "var(--color-Field-Border-normal)",
                             baseURL         = ""            ,
                             extension       = ""            ,
                             showPreview     = true          ,
                             autoPlay        = false         ,
                             library         = false         ,
                             upload          = true          ,
                             showTextInput   = true          ,
                             previewWidth                    ,
                             fileName                        }: FieldSoundProps) => {

  const fieldStyle = {
    "--glow-color": glowColor,
    "--icon-square-color": iconSquareColor,
  } as React.CSSProperties;

  const [ mediaSrc   , setMediaSrc   ] = useState("");
  const [ inputValue , setInputValue ] = useState(value);
  const [ isBlob     , setIsBlob     ] = useState(false);
  const [ audioError , setAudioError ] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef   = useRef<string | null>(null);

  React.useEffect(() => {
    return () => {
      if (blobUrlRef.current) 
	   {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
       }
    };
  }, []);

  // Synchronisation avec la prop value
  React.useEffect(() => {
    if (value && typeof value === "string" && !isBlob) {
      const fullPath = constructFullPath(value, baseURL, extension);
      setMediaSrc(fullPath);
      setInputValue(value);
    }
  }, [value, baseURL, extension, isBlob]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // `accept="audio/*"` ne filtre que la boîte de dialogue : « Tous les fichiers » la
    // contourne. Le refus était déjà là mais muet (console) : il remonte désormais au
    // parent. Le type déclaré par le navigateur fait foi ; l'extension ne sert que
    // lorsqu'il est vide.
    const estUnSon = file.type ? file.type.startsWith('audio/')
                               : getMediaType(file.name) === 'Sound';
    if (!estUnSon) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onFileRejected) onFileRejected(file);
      return;
    }
    // Nettoyer l'ancienne URL blob si elle existe
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    // Créer une nouvelle URL blob
    const objectURL = URL.createObjectURL(file);
    blobUrlRef.current = objectURL;
    setInputValue(file.name);
    setMediaSrc(objectURL);
    setIsBlob(true);
    setAudioError(false);
    if (onChange) {
      onChange(event, file);
    }
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setInputValue(input);
    setAudioError(false);
    // Si l'utilisateur tape manuellement, on sort du mode blob
    if (isBlob) {
      setIsBlob(false);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    }
    // Construire le chemin complet
    if (input) {
      const fullPath = constructFullPath(input, baseURL, extension);
      setMediaSrc(fullPath);
    } else {
      setMediaSrc("");
    }
    if (onChange) {
      onChange(event, input);
    }
  };

  const handleUploadClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleLibraryClick = () => {
    if (readOnly) return;
    if (onShowLibrary) {
      onShowLibrary();
    }
  };

  const handleDeleteClick = () => {
    if (readOnly) return;

    // Révoquer l'ancienne URL blob si présente
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // Réinitialiser tous les états liés au média
    setMediaSrc("");
    setInputValue("");
    setIsBlob(false);
    setAudioError(false);

    // Réinitialiser l'input file (pour permettre un nouvel upload du même fichier)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (onDelete) {
      onDelete();
    }
  };

  const handleAudioError = () => {
    setAudioError(true);
  };

  const constructFullPath = (value: string, baseURL: string, extension: string): string => {
    if (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
    let fullPath = value;
    if (!value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      fullPath += extension || ".mp3";
    }

    console.log(fullPath);

    return fullPath;
  };

  // Carré contenant une icône (upload, library3, trashCan...), même classe que Field.tsx / FieldMedia.tsx
  const IconSquare = ({
    children,
    onClick,
  }: {
    children: React.ReactNode;
    onClick?: () => void;
  }) => (
    <div className={classNames(styles.icon_square)} onClick={onClick}>
      {children}
    </div>
  );

  // Fonction renderPreview avec le composant ProgressBar
  const renderPreview = (): JSX.Element | null => {
    const srcToPlay  = isBlob ? mediaSrc : (mediaURL || mediaSrc);

    if (!showPreview) return null;

    // Affichage de la progression pendant le chargement avec le composant ProgressBar
    if (isLoading && loadProgress) {
      return <ProgressBar progress={loadProgress} height={32} />;
    }

    // Pas de source disponible : plutôt qu'un trou, la même invitation que les champs
    // d'image — un nuage et le mot « Parcourir ». Le bloc entier ouvre le sélecteur,
    // que l'input caché filtre sur « audio/* » : on ne choisit pas une image ici.
    if (!srcToPlay) {
      return (
        <div className={classNames(styles.sound_placeholder)}
             onClick={readOnly ? undefined : handleUploadClick}>
          <Icon name       = "cloud"
                color      = "var(--color-Field-Icon-normal)"
                hoverColor = "var(--color-Field-Icon-hover-normal)"
                width      = {28}
                height     = {28} />
          <span>Parcourir</span>
        </div>
      );
    }

    // Erreur de chargement
    if (audioError) {
      return (
        <div className={classNames(styles.message, styles.error)}>
          Impossible de charger le fichier audio
        </div>
      );
    }

    // Affichage du player audio.
    // Ce fut longtemps un <audio controls>, dont l'habillage sombre ne tenait que
    // sur les pseudo-éléments ::-webkit-media-controls-* : ils ne sont pas honorés
    // de la même façon sur Mac, où la barre revenait claire et illisible. SoundPlayer
    // dessine les mêmes commandes en DOM ordinaire — voir son en-tête.
    return (
      <div className={classNames(styles.audio_player)}>
        <SoundPlayer src      = {srcToPlay}
                     autoPlay = {autoPlay}
                     onError  = {handleAudioError} />
      </div>
    );
  };

  // Le lecteur, rendu une seule fois : sa place change selon qu'on affiche ou non la
  // zone de texte, mais c'est le même élément — le reconstruire à deux endroits
  // remonterait l'<audio> et couperait la lecture en cours.
  const preview = renderPreview();

  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      <div className={classNames(styles.file_input_wrapper)}>
        <div className={classNames(styles.field_with_icon, previewWidth && styles.field_with_icon_wide_gap)}>
          {/* Input file caché */}
          <input ref       = {fileInputRef}
                 type      = "file"
                 className = {classNames(styles.file_input_hidden)}
                 accept    = "audio/*"
                 onChange  = {handleFileChange}
                 disabled  = {readOnly}
          />
          {
           /* Sans zone de texte, c'est le lecteur qui occupe la ligne et les icônes
              se rangent à sa droite. Le conteneur reste là même quand il n'y a rien
              à lire : sans lui, les icônes glisseraient à gauche tant qu'aucun son
              n'est choisi, puis sauteraient à droite au premier fichier. */
           showTextInput
             ? <input type        = "text"
                      className   = {classNames(styles.field_input)}
                      placeholder = {placeholder}
                      value       = {typeof inputValue === "string" ? inputValue : ""}
                      readOnly    = {readOnly}
                      onChange    = {handleTextChange} />
             : <div className = {classNames(styles.inline_preview)}
                    style     = {previewWidth ? { flex: `0 0 ${previewWidth}` } : undefined}>
                 {/* Le nom coiffe le lecteur : il se retrouve à la hauteur des icônes,
                     rangées à droite, et le lecteur descend d'autant. */}
                 {fileName && (
                   <div className={classNames(styles.file_name)} title={fileName}>{fileName}</div>
                 )}
                 {preview}
               </div>
          }
          {/* Regroupées plutôt que posées à la suite du champ : face à un aperçu de
              largeur fixe, le groupe prend la place qui reste et y centre les icônes,
              comme le fait FieldMediaPendingImage. Sans aperçu de largeur fixe le
              groupe se contente de sa taille, et le rendu est celui d'avant. */}
          <div className={classNames(styles.icon_group, previewWidth && styles.icon_group_spread)}>
            {
             !readOnly &&
              upload &&
              <IconSquare onClick={handleUploadClick}>
                <Icon name       = "upload"
                      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)"
                      width      = {18}
                      height     = {18} />
              </IconSquare>
            }
            {
             !readOnly &&
              library &&
              <IconSquare onClick={handleLibraryClick}>
                <Icon name       = "library3"
                      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)"
                      width      = {18}
                      height     = {18} />
              </IconSquare>
            }
            {
             !readOnly &&
              onDelete &&
              <IconSquare onClick={handleDeleteClick}>
                <Icon name       = "trashCan"
                      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)"
                      width      = {18}
                      height     = {18} />
              </IconSquare>
            }
          </div>
        </div>
        {/* Prévisualisation / Player — sous la ligne quand la zone de texte l'occupe */}
        {showTextInput && preview}
      </div>
    </div>
  );
};

export default FieldSound;